import { couponBenefitHeadline, couponBenefitLabel } from "@/lib/coupon-benefit"
import { couponNotUsableMessage, type CouponTerms } from "@/lib/coupon-readiness"
import { addCouponCode, type AddCouponCodeResult } from "@/lib/discount-evaluation/coupon-codes"
import { describeRejection } from "@/lib/discount-evaluation/coupon-copy"
import type { CartLinePayload, CouponEvaluation, RejectedCoupon } from "@/lib/discount-evaluation/types"
import { centsToReais, formatBRL } from "@/lib/format-brl"
import {
  attachedCouponCodes,
  couponCodeKey,
  dropPendingCouponCode,
  forgetKeptCoupons,
  getPendingCouponCodes,
  keepCouponForLater,
  savePendingCouponCodes,
} from "@/lib/pending-coupon"
import type { Cart } from "@/types"

/**
 * Where one code stands on the bag. `minimum` still misses the coupon's
 * minimum order (`missingCents` null when the record does not say how much),
 * `applied` is what the bag verdict takes off (the checkout revalidates it),
 * `at-checkout` holds once the
 * delivery fee is known (`delivery`) or once the checkout confirms it
 * (`unconfirmed`), `blocked` needs a bag change, `not-usable` will not apply
 * now, `dropped` lost to a better combination.
 */
export type BagCouponStatus =
  | { kind: "checking" }
  | { kind: "minimum"; missingCents: number | null }
  | { kind: "applied"; discountCents: number; capped: boolean }
  | { kind: "at-checkout"; reason: "delivery" | "unconfirmed" }
  | { kind: "blocked"; hints: string[] }
  | { kind: "not-usable"; message: string }
  | { kind: "dropped"; note: string }

/** One code on the bag as the strip and the pill draw it; `minimum.progress` is the 0..1 share of the minimum order already in the bag. */
export type BagCoupon = {
  code: string
  benefitLabel: string | null
  benefitHeadline: { headline: string; detail: string } | null
  terms: CouponTerms | null
  validity: string | null
  minimum: { cents: number; progress: number } | null
  status: BagCouponStatus
  updating: boolean
}

/** The last verdict and whether it was given for the bag on screen. */
export type BagVerdict = { evaluation: CouponEvaluation; fresh: boolean }

/** What {@link bagCouponView} reads; `terms` is undefined while the public record is still being read. */
export type BagCouponInput = {
  code: string
  terms: CouponTerms | null | undefined
  verdict: BagVerdict | null
  unconfirmed: boolean
  subtotalCents: number
  cart: Cart
  now: Date
  timeZone: string
}

function dayKey(at: Date, timeZone: string): string {
  return new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", timeZone }).format(at)
}

/** A coupon's expiry in a few words beside its state ("até 31/10", "até 18h16" on its last day), on the store's clock; null when absent or past. */
export function couponValidityShort(expiresAt: string | null, timeZone: string, now: Date): string | null {
  const at = expiresAt ? new Date(expiresAt) : null
  if (!at || Number.isNaN(at.getTime()) || at.getTime() <= now.getTime()) return null
  const parts = new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZone }).formatToParts(at)
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((entry) => entry.type === type)?.value ?? ""
  if (dayKey(at, timeZone) !== dayKey(now, timeZone)) return `até ${part("day")}/${part("month")}`
  const minute = part("minute")
  return `até ${part("hour")}h${minute === "00" ? "" : minute}`
}

/** The bag total coupon-service measures a minimum order against: every line at its menu price, before any discount (integer cents). */
export function evaluationSubtotalCents(lines: readonly CartLinePayload[]): number {
  return lines.reduce((sum, line) => {
    const options = line.options.reduce((acc, option) => acc + option.quantity * option.unitPriceCents, 0)
    return sum + line.quantity * (line.unitPriceCents + options)
  }, 0)
}

function allocatedCents(evaluation: CouponEvaluation, code: string): number {
  return evaluation.allocations
    .filter((allocation) => couponCodeKey(allocation.couponCode) === couponCodeKey(code))
    .reduce((sum, allocation) => sum + allocation.amountCents, 0)
}

function rejectedStatus(input: BagCouponInput, rejected: RejectedCoupon, evaluation: CouponEvaluation, missingCents: number): BagCouponStatus {
  const { code, terms, cart, now } = input
  if (rejected.reason === "EVALUATION_REASON_MIN_PURCHASE_NOT_MET") {
    return { kind: "minimum", missingCents: missingCents > 0 ? missingCents : null }
  }
  if (rejected.reason === "EVALUATION_REASON_NO_TARGET_IN_CART" && terms?.benefit.kind === "delivery") {
    return { kind: "at-checkout", reason: "delivery" }
  }
  if (rejected.reason === "EVALUATION_REASON_OUTSIDE_AVAILABILITY") {
    const message = couponNotUsableMessage(code, { status: "unavailable-now", availability: terms?.availability ?? null }, now)
    if (message) return { kind: "not-usable", message }
  }
  const notice = describeRejection(rejected, cart, evaluation.maxCouponsPerOrder)
  if (notice.kind === "invalid") return { kind: "not-usable", message: notice.message }
  if (notice.kind === "dropped") return { kind: "dropped", note: notice.note }
  return { kind: "blocked", hints: notice.blockers.map((blocker) => blocker.text) }
}

function verdictStatus(input: BagCouponInput, verdict: BagVerdict, missingCents: number): BagCouponStatus | null {
  const { evaluation } = verdict
  const key = couponCodeKey(input.code)
  const discountCents = allocatedCents(evaluation, input.code)
  if (discountCents > 0) {
    const cap = input.terms?.maxDiscountCents ?? 0
    return { kind: "applied", discountCents, capped: cap > 0 && discountCents >= cap }
  }
  if (evaluation.applied.some((coupon) => couponCodeKey(coupon.code) === key)) {
    return { kind: "at-checkout", reason: input.terms?.benefit.kind === "delivery" ? "delivery" : "unconfirmed" }
  }
  const rejected = evaluation.rejected.find((coupon) => couponCodeKey(coupon.code) === key)
  return rejected ? rejectedStatus(input, rejected, evaluation, missingCents) : null
}

/**
 * How one code reads on the bag. The verdict is coupon-service's; the bag
 * only measures how far the minimum order from the coupon's public record
 * still is. While a newer verdict is on its way the last one stays on screen
 * (`updating`), except that a bag that fell below the minimum shows the
 * missing amount at once and one that just reached it waits for the server.
 */
export function bagCouponView(input: BagCouponInput): BagCoupon {
  const { code, terms, verdict, unconfirmed, subtotalCents } = input
  const minimumCents = terms?.minPurchaseCents ?? 0
  const missingCents = Math.max(0, minimumCents - subtotalCents)
  const base = {
    code,
    benefitLabel: terms ? couponBenefitLabel(terms.benefit, terms.reward, terms.offer) : null,
    terms: terms ?? null,
    benefitHeadline: terms ? couponBenefitHeadline(terms.benefit, terms.reward, terms.maxDiscountCents, terms.offer) : null,
    validity: couponValidityShort(terms?.expiresAt ?? null, input.timeZone, input.now),
    minimum: minimumCents > 0 ? { cents: minimumCents, progress: Math.min(1, subtotalCents / minimumCents) } : null,
  }
  if (terms === undefined) return { ...base, status: { kind: "checking" }, updating: true }
  if (verdict) {
    const status = verdictStatus(input, verdict, missingCents)
    if (verdict.fresh) return { ...base, status: status ?? { kind: "at-checkout", reason: "unconfirmed" }, updating: false }
    if (minimumCents > 0 && missingCents > 0) return { ...base, status: { kind: "minimum", missingCents }, updating: true }
    if (!status || status.kind === "minimum") return { ...base, status: { kind: "checking" }, updating: true }
    return { ...base, status, updating: true }
  }
  if (minimumCents > 0 && missingCents > 0) return { ...base, status: { kind: "minimum", missingCents }, updating: !unconfirmed }
  if (unconfirmed) return { ...base, status: { kind: "at-checkout", reason: "unconfirmed" }, updating: false }
  return { ...base, status: { kind: "checking" }, updating: true }
}

/** What the applied coupons on the bag take off, in cents (the amounts coupon-service allocated). */
export function bagCouponDiscountCents(coupons: readonly BagCoupon[]): number {
  return coupons.reduce((sum, coupon) => sum + (coupon.status.kind === "applied" ? coupon.status.discountCents : 0), 0)
}

const HEADLINE_ORDER: Record<BagCouponStatus["kind"], number> = {
  applied: 0,
  minimum: 1,
  "at-checkout": 2,
  blocked: 3,
  "not-usable": 4,
  dropped: 5,
  checking: 6,
}

/** The one coupon the bag and the checkout show: the applied one (or every applied one when they combine), else the one closest to applying. */
export type BagCouponFocus = { kind: "single"; coupon: BagCoupon } | { kind: "group"; coupons: BagCoupon[]; discountCents: number }

/** Picks what to show out of the coupons on the order (see {@link BagCouponFocus}); null without coupons. */
export function bagCouponFocus(coupons: readonly BagCoupon[]): BagCouponFocus | null {
  const applied = coupons.filter((coupon) => coupon.status.kind === "applied")
  if (applied.length > 1) return { kind: "group", coupons: applied, discountCents: bagCouponDiscountCents(applied) }
  const [first] = [...coupons].sort((a, b) => HEADLINE_ORDER[a.status.kind] - HEADLINE_ORDER[b.status.kind])
  return first ? { kind: "single", coupon: first } : null
}

/** What the bag pill says for the focused coupon(s): its benefit ("10% OFF até R$ 15", else the code, or "2 cupons") and its few words; null without codes. */
export function bagCouponHeadline(
  coupons: readonly BagCoupon[],
): { code: string; title: string; text: string; tone: "success" | "pending" | "warning" } | null {
  const focus = bagCouponFocus(coupons)
  if (!focus) return null
  if (focus.kind === "group") {
    return {
      code: focus.coupons.map((coupon) => coupon.code).join(", "),
      title: `${focus.coupons.length} cupons`,
      text: formatBRL(centsToReais(focus.discountCents), { sign: "minus" }),
      tone: "success",
    }
  }
  const { coupon } = focus
  return { code: coupon.code, title: coupon.benefitHeadline?.headline ?? coupon.code, ...headlineWords(coupon) }
}

function headlineWords({ status }: BagCoupon): { text: string; tone: "success" | "pending" | "warning" } {
  switch (status.kind) {
    case "applied":
      return { text: formatBRL(centsToReais(status.discountCents), { sign: "minus" }), tone: "success" }
    case "minimum":
      return {
        text: status.missingCents === null ? "falta o valor mínimo" : `faltam ${formatBRL(centsToReais(status.missingCents))}`,
        tone: "pending",
      }
    case "at-checkout":
      return { text: status.reason === "delivery" ? "liberado" : "no pedido", tone: "success" }
    case "blocked":
      return { text: "ajuste a sacola", tone: "warning" }
    case "not-usable":
      return { text: "não vale agora", tone: "warning" }
    case "dropped":
      return { text: "não aplicado", tone: "warning" }
    case "checking":
      return { text: "conferindo…", tone: "pending" }
  }
}

/**
 * Puts a typed code on this visit's order (in place of `replacing`, when
 * given): normalized and deduplicated like the checkout field, stashed so the
 * checkout picks it up, and kept in the customer's wallet.
 */
export function attachBagCouponCode(merchantId: string, raw: string, replacing?: string): AddCouponCodeResult {
  const others = attachedCouponCodes(merchantId).filter((code) => code !== replacing)
  const result = addCouponCode(others, raw)
  if (!result.ok) return result
  if (replacing) detachBagCouponCode(merchantId, replacing)
  savePendingCouponCodes([...getPendingCouponCodes(merchantId), result.code], merchantId)
  keepCouponForLater(result.code, merchantId)
  return result
}

/** Takes a code off this visit's order; it stays in the customer's wallet. */
export function detachBagCouponCode(merchantId: string, code: string): void {
  dropPendingCouponCode(code, merchantId)
}

/** Takes a code that will never work for this customer off the order and out of the wallet kept on this device. */
export function forgetCouponCode(merchantId: string, code: string): void {
  dropPendingCouponCode(code, merchantId)
  forgetKeptCoupons([code], merchantId)
}
